import { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * StatusChangeModal - Production-quality modal for changing employee status
 *
 * Design: Matches EmployeeSlideout quality with fixed height and internal scrolling
 *
 * Features:
 * - Fixed max-height prevents expansion
 * - Internal scrolling for dynamic content
 * - Auto-scroll to termination reason when "Terminated" selected
 * - Smooth animations matching slideout
 * - Mobile-responsive (full screen on mobile, centered modal on desktop)
 * - Enhanced visual feedback for termination
 * - Backdrop blur effect
 */
const StatusChangeModal = ({ employee, onSubmit, onCancel }) => {
  const [newStatus, setNewStatus] = useState(employee.employmentStatus);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Refs for auto-scrolling
  const scrollContainerRef = useRef(null);
  const terminationSectionRef = useRef(null);

  // Auto-scroll to termination section when status changes to terminated
  useEffect(() => {
    if (newStatus === 'terminated' && terminationSectionRef.current && scrollContainerRef.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        terminationSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100);
    }
  }, [newStatus]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate termination reason
    if (newStatus === 'terminated' && !reason.trim()) {
      setError('Reason is required for termination');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onSubmit(employee.id, newStatus, reason.trim() || null);
    } catch (err) {
      console.error('Status change error:', err);
      setError('Failed to update employee status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: 'Active', description: 'Currently employed and working' },
    { value: 'inactive', label: 'Inactive', description: 'Temporarily not working (leave, suspension)' },
    { value: 'terminated', label: 'Terminated', description: 'Employment ended' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200';
      case 'inactive': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'terminated': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        {/* Backdrop with blur */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all w-full sm:my-8 sm:w-full sm:max-w-lg">
                {/* Fixed Header */}
                <div className="bg-white px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Change Employment Status
                    </Dialog.Title>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                        onClick={onCancel}
                        disabled={loading}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Scrollable Content - KEY FIX: max-h with overflow-y-auto */}
                <div ref={scrollContainerRef} className="max-h-[calc(100vh-16rem)] sm:max-h-[28rem] overflow-y-auto">
                  <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
                    {/* Employee Info */}
                    <div className="flex items-center gap-3">
                      {/* Avatar with gradient */}
                      <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-sm shadow-md">
                        {employee.firstName?.[0]}{employee.lastName?.[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-base font-semibold text-gray-900 truncate">
                          {employee.firstName} {employee.lastName}
                        </h4>
                        <p className="text-sm text-gray-500 truncate">{employee.jobTitle || 'Employee'}</p>
                      </div>
                    </div>

                    {/* Current Status */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Current Status</p>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shadow-sm ${getStatusColor(employee.employmentStatus)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          employee.employmentStatus === 'active' ? 'bg-green-500' :
                          employee.employmentStatus === 'inactive' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`} />
                        {employee.employmentStatus?.charAt(0).toUpperCase() + employee.employmentStatus?.slice(1)}
                      </span>
                    </div>

                    {/* New Status Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        New Status
                      </label>
                      <div className="space-y-3">
                        {statusOptions.map((option) => (
                          <label
                            key={option.value}
                            className={`relative flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              newStatus === option.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center h-5">
                              <input
                                type="radio"
                                name="status"
                                value={option.value}
                                checked={newStatus === option.value}
                                onChange={(e) => setNewStatus(e.target.value)}
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                disabled={loading}
                              />
                            </div>
                            <div className="ml-3 flex-1">
                              <span className={`block text-sm font-medium ${
                                option.value === 'active' ? 'text-green-700' :
                                option.value === 'inactive' ? 'text-yellow-700' :
                                'text-red-700'
                              }`}>
                                {option.label}
                              </span>
                              <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Termination Reason (conditional) */}
                    {newStatus === 'terminated' && (
                      <div ref={terminationSectionRef} className="space-y-4">
                        {/* Warning Banner */}
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                          <div className="flex gap-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-amber-900">
                                Termination Notice
                              </h4>
                              <p className="text-sm text-amber-700 mt-1">
                                This action will immediately revoke all system access and set retention policies.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Reason Textarea */}
                        <div>
                          <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                            Reason for Termination <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="reason"
                            name="reason"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                              error && !reason.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="Voluntary resignation, accepted position with competitor..."
                            disabled={loading}
                          />
                          {error && !reason.trim() && (
                            <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                              <ExclamationTriangleIcon className="h-4 w-4" />
                              {error}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* General Error */}
                    {error && newStatus !== 'terminated' && (
                      <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}
                  </form>
                </div>

                {/* Fixed Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className={`px-4 py-2.5 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 ${
                      newStatus === 'terminated'
                        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    }`}
                    disabled={loading || (newStatus === 'terminated' && !reason.trim())}
                  >
                    {loading && (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {newStatus === 'terminated' ? 'Terminate Employee' : 'Update Status'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default StatusChangeModal;
